<template>
    <JobsTable v-if="$can('view', 'production')" :title="t('menu.production')" icon="tabler-map" :headers="headers"
        type="production" :show-actions-menu="false">
        <!-- Custom slots for production-specific columns -->
        <template #item.production_plate_type="{ item }">
            {{ item.production_plate_type === 1 ? 'ESE' : item.production_plate_type === 3 ? 'X' : 'DPI' }}
        </template>

        <template #item.ph="{ item }">
            <div class="d-flex align-center gap-2 ph-copy-container" style="position: relative;">
                <span class="font-weight-bold text-primary">PH{{ item.ph || '' }}</span>
                <VIcon v-if="item.is_urgent" icon="tabler-bolt" color="error" size="26" />
                <VIcon v-if="item.ph" icon="tabler-copy" size="16" class="ph-copy-icon"
                    style="opacity: 0; transition: opacity 0.2s; cursor: pointer;"
                    @click="copyToClipboard(`PH${item.ph}`)" />
            </div>
        </template>

        <template #item.production_read="{ item }">
            <VIcon icon="tabler-flip-vertical" v-if="item.production_read" color="error" size="26" />
        </template>
    </JobsTable>
    <VAlert v-else type="warning" variant="tonal">
        {{ $t('admin_notifications.no_permission') }}
    </VAlert>

    <!-- 👉 Notification Snackbar -->
    <VSnackbar v-model="notification.show" :color="notification.type" :timeout="notification.timeout" location="top end"
        @click="hideNotification">
        <div class="d-flex align-center">
            <VIcon :icon="getNotificationIcon(notification.type)" class="me-2" />
            <div>
                <div class="font-weight-medium">{{ notification.title }}</div>
                <div class="">{{ notification.message }}</div>
            </div>
        </div>

        <template #actions>
            <VBtn icon="tabler-x" size="small" variant="text" @click.stop="hideNotification" />
        </template>
    </VSnackbar>
</template>

<script setup>

import JobsTable from '@/components/polyhen/jobs/JobsTable.vue'
import { useHead } from '@unhead/vue'

definePage({
    meta: {
        action: 'view',
        subject: 'production',
    },
})
const { t } = useI18n()

// Set page title
useHead({
    title: t('admin_notifications.production'),
    meta: [
        { name: 'description', content: t('admin_notifications.production_description') }
    ]
})
// Copy to clipboard function
const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text)
        showNotification('success', t('admin_notifications.success'), t('admin_notifications.copied_to_clipboard'))
    } catch (err) {
        console.error('Failed to copy text: ', err)
        showNotification('error', t('admin_notifications.error'), t('admin_notifications.copy_failed'))
    }
}
// Headers for production jobs table
const headers = computed(() => [
    {
        title: 'ID',
        key: 'id',
        sortable: false,
        width: '80px',
        align: 'right'
    },
    {
        title: 'PH',
        key: 'ph',
        sortable: false,
        width: '100px',
        align: 'right'
    },
    {
        title: t('admin_notifications.order'),
        key: 'order',
        sortable: false,
        width: '250px',
    },
    {
        title: t('admin_notifications.inside'),
        key: 'production_read',
        sortable: false,
        width: '80px',
    },
    {
        title: t('admin_notifications.plate'),
        key: 'production_plate_thickness',
        sortable: false,
        width: '80px',
    },
    {
        title: t('admin_notifications.type'),
        key: 'production_plate_type',
        sortable: false,
        width: '80px',
    },
    {
        title: t('admin_notifications.customer'),
        key: 'customer',
        sortable: false,
        width: '150px',
    },
    {
        title: t('admin_notifications.printer'),
        key: 'printer',
        sortable: false,
        width: '150px',
    },
    {
        title: t('admin_notifications.assigned'),
        key: 'assigned',
        sortable: false,
        width: '120px',
    },
    // {
    //     title: t('admin_notifications.edit_date'),
    //     key: 'created_at',
    //     sortable: false,
    //     width: '140px',

    // },
    {
        title: t('admin_notifications.status'),
        key: 'status',
        sortable: false,
        width: '140px',
    },
    {
        title: t('admin_notifications.actions'),
        key: 'actions',
        sortable: false,
        width: '100px',
    },
])
const notification = ref({
    show: false,
    type: 'success',
    title: '',
    message: '',
    timeout: 5000
})

const showNotification = (type, title, message, timeout = 5000) => {
    notification.value = {
        show: true,
        type,
        title,
        message,
        timeout
    }
}

const hideNotification = () => {
    notification.value.show = false
}

const getNotificationIcon = (type) => {
    const icons = {
        success: 'tabler-check',
        error: 'tabler-x',
        warning: 'tabler-alert-triangle',
        info: 'tabler-info-circle'
    }
    return icons[type] || 'tabler-info-circle'
}
</script>

<style scoped>
.ph-copy-container:hover .ph-copy-icon {
    opacity: 1 !important;
}

.ph-copy-icon:hover {
    color: rgb(var(--v-theme-primary)) !important;
}
</style>